import type { FC } from 'react';
import type { ChainFamily } from './chains';

import React, { lazy, Suspense } from 'react';

import { TronWalletButton } from './sdk.client';

// Same reasoning as blockchain/provider.tsx: a family this build does not enable comes in through
// a dynamic import behind a literal build constant, so its wallet SDK is neither run nor bundled.
// On the server there is no wallet to connect: it renders no button of either family.
const EvmWalletButton =
  __EVM_ENABLED__ && !import.meta.env.SSR
    ? lazy(() => import('./evm/EvmWalletButton').then((m) => ({ default: m.EvmWalletButton })))
    : null;

interface WalletButtonProps {
  /**
   * Wallet family to connect — always named by the caller (app#243). It used to default to the
   * build's default family, TRON, and four screens took that default: a buyer whose only wallet
   * is MetaMask was offered a button no wallet of theirs could answer, including right next to a
   * sentence naming their EVM address. A screen either knows the family (the wallet the account
   * already holds, the network of the deal) or offers one slot per family; "TRON unless told
   * otherwise" is not a third option, and the required prop keeps it from coming back.
   */
  family: ChainFamily;
}

export const WalletButton: FC<WalletButtonProps> = ({ family }) => {
  if (family === 'evm' && EvmWalletButton) {
    return (
      <Suspense fallback={null}>
        <EvmWalletButton />
      </Suspense>
    );
  }

  if (family === 'tron' && __TRON_ENABLED__ && !import.meta.env.SSR) {
    return <TronWalletButton />;
  }

  // A family that is not part of this build has no connect button to offer. Rendering nothing is
  // the honest outcome: the chain list the user chooses from comes from api `/buyer/chains`, so a
  // chain this build cannot serve should never have been offered in the first place.
  return null;
};
